import { Check, ChevronDown, Copy } from 'lucide-react';
import { useState } from 'react';

/**
 * Documentação dos endpoints da API externa (estilo Swagger). Catálogo em grupos
 * colapsáveis; cada endpoint abre detalhes (parâmetros + exemplo). Pensado pra
 * crescer: novos endpoints entram no array ENDPOINT_GROUPS.
 */

/** Base pública absoluta — em prod é o host do painel (novo.chatplusbr.com). */
function apiBase(): string {
  return typeof window !== 'undefined' ? window.location.origin : '';
}

type ParamLocation = 'path' | 'query' | 'header' | 'body';
interface Param {
  name: string;
  type: string;
  location: ParamLocation;
  required?: boolean;
  desc: string;
}
interface EndpointDoc {
  method: 'GET' | 'POST';
  path: string;
  desc: string;
  params?: Param[];
  example?: string;
}
interface EndpointGroup {
  title: string;
  subtitle: string;
  endpoints: EndpointDoc[];
}

const ENDPOINT_GROUPS: EndpointGroup[] = [
  {
    title: 'Atendimentos',
    subtitle: 'Leitura de conversas para automações e integrações',
    endpoints: [
      {
        method: 'GET',
        path: '/api/v1/external/conversations/{protocolo}',
        desc: 'Retorna a conversa completa de um atendimento pelo número do protocolo. Texto vem em "text"; áudio/imagem/PDF vêm em "file_url" (URL assinada, baixa sem login) + "file_mimetype". O protocolo chega no webhook de fechamento (Push) como protocol_number.',
        params: [
          {
            name: 'protocolo',
            type: 'string',
            location: 'path',
            required: true,
            desc: 'Número do protocolo do atendimento — o mesmo que aparece no Histórico de Chat (ex: 000231). Aceita com ou sem zeros à esquerda. Vem no webhook como ticket.protocol_number.',
          },
          {
            name: 'Authorization',
            type: 'string',
            location: 'header',
            required: true,
            desc: 'Bearer SUA_CHAVE — a chave é criada por um administrador na aba "Chave de API".',
          },
        ],
        example: `{
  "id_chatplus": 1133,
  "tenant": { "id": 1, "name": "Minha Empresa", "slug": "minha-empresa" },
  "ticket": {
    "id": 1133, "protocol_number": "000267", "status": "closed",
    "opened_at": "2026-06-19T18:49:17.730Z", "closed_at": "2026-06-20T16:58:34.547Z",
    "channel": { "id": 41, "display_name": "Atendimento", "display_number": "556132545598" }
  },
  "contact": { "id": 952, "name": "Felipe", "phone": "558598014594" },
  "count": 9, "truncated": false,
  "messages": [
    { "direction": "inbound",  "type": "text",  "text": "Boa tarde",
      "file_mimetype": null, "file_url": null,
      "messagetimestamp": "2026-06-19T18:49:17.732Z", "status": "received" },
    { "direction": "outbound", "type": "image", "text": null,
      "file_mimetype": "image/jpeg",
      "file_url": "https://.../api/v1/proxy/messages/26096/media?tid=1&v=...&t=...",
      "messagetimestamp": "2026-06-19T18:52:00.000Z", "status": "read" }
  ]
}`,
      },
    ],
  },
  {
    title: 'Envio de mensagens (disparo externo)',
    subtitle: 'Sistema externo dispara templates pelo ChatPlus — 1 chamada por destinatário',
    endpoints: [
      {
        method: 'POST',
        path: '/api/v1/external/messages',
        desc: 'Envia um template HSM aprovado pra um telefone (canal Cloud API). Não cria atendimento: se o contato JÁ TEM uma conversa aberta no canal, a mensagem entra nela (o atendente vê ao vivo); senão, entra no histórico do contato e, se ele responder, o atendimento abre normalmente. Resposta 202 sem wamid (envio é assíncrono) — o funil (entregue/lido/falha/respondido) volta pelos webhooks Push "message.status" e "message.replied", que ecoam o correlation_id enviado aqui (configure o webhook na aba Push).',
        params: [
          {
            name: 'Authorization',
            type: 'string',
            location: 'header',
            required: true,
            desc: 'Bearer SUA_CHAVE — a chave é criada por um administrador na aba "Chave de API".',
          },
          {
            name: 'whatsapp_number_id',
            type: 'number',
            location: 'body',
            required: true,
            desc: 'Canal (número) de onde a mensagem sai — liste em GET /external/channels. Precisa ser Cloud API (oficial) e estar ativo.',
          },
          {
            name: 'to',
            type: 'string',
            location: 'body',
            required: true,
            desc: 'Telefone destino em E.164 sem "+" (ex: 5561999998888). Contato é criado automaticamente se não existir.',
          },
          {
            name: 'template',
            type: 'object',
            location: 'body',
            required: true,
            desc: '{ "name": "...", "language": "pt_BR" } — template APROVADO da WABA do canal (liste em GET /external/templates).',
          },
          {
            name: 'variables',
            type: 'string[]',
            location: 'body',
            desc: 'Valores posicionais: variables[0] → {{1}}, variables[1] → {{2}}... Obrigatório quando o template tem variáveis (variable_count).',
          },
          {
            name: 'correlation_id',
            type: 'string',
            location: 'body',
            desc: 'Id do destinatário NO SEU sistema — ecoado nos webhooks message.status/message.replied pra você casar o funil.',
          },
          {
            name: 'idempotency_key',
            type: 'string',
            location: 'body',
            desc: 'Chave de deduplicação: reenviar com a mesma chave NÃO duplica o disparo — devolve a mensagem existente com o status real (queued/sent/failed).',
          },
          {
            name: 'contact_name',
            type: 'string',
            location: 'body',
            desc: 'Nome pro contato quando ele ainda não existe (não sobrescreve nome já cadastrado).',
          },
          {
            name: 'header_media',
            type: 'object',
            location: 'body',
            desc: '{ "url": "https://...", "type": "image|video|document", "filename": "..." } — mídia do cabeçalho POR DISPARO. O ChatPlus baixa da URL na hora do POST, valida os limites da Meta (imagem 5MB, vídeo 16MB, documento 100MB) e re-hospeda no próprio storage — a mesma URL em vários disparos baixa uma vez só. OBRIGATÓRIO quando o template tem cabeçalho de mídia (imagem/vídeo/documento) — e do mesmo tipo; sem ele a requisição volta 422 HEADER_MEDIA_REQUIRED. "filename" só pra document (sem ele o WhatsApp mostra "Sem Título").',
          },
        ],
        example: `// Requisição
{
  "whatsapp_number_id": 12,
  "to": "5561999998888",
  "template": { "name": "mf_promo_v1", "language": "pt_BR" },
  "variables": ["João", "BLACK10"],
  "correlation_id": "dest-1001",
  "idempotency_key": "camp42-dest-1001"
}

// Resposta 202 (novo)           → { "id": 98421, "status": "queued" }
// Resposta 200 (chave repetida) → { "id": 98421, "status": "sent", "duplicate": true }

// Depois, via webhook Push (configure na aba Push):
// message.status  → { "correlation_id": "dest-1001", "status": "delivered", "wamid": "...", "at": "..." }
// message.replied → { "correlation_id": "dest-1001", "at": "..." }  (1ª resposta do destinatário)`,
      },
      {
        method: 'GET',
        path: '/api/v1/external/templates',
        desc: 'Lista os templates HSM do tenant (nome, idioma, status, categoria, nº de variáveis, tipo de cabeçalho, texto do corpo e o array "components" cru no shape da Meta — use-o pra detectar header de mídia, renderizar preview e derivar variáveis). Só templates com status "approved" podem ser enviados.',
        params: [
          {
            name: 'Authorization',
            type: 'string',
            location: 'header',
            required: true,
            desc: 'Bearer SUA_CHAVE.',
          },
          {
            name: 'whatsapp_number_id',
            type: 'number',
            location: 'query',
            desc: 'Filtra pelos templates da WABA deste canal — recomendado: template de outra WABA não envia por este canal.',
          },
        ],
        example: `{
  "templates": [
    {
      "meta_template_id": "1284969470391505",
      "name": "mf_promo_v1", "language": "pt_BR", "status": "approved",
      "category": "MARKETING", "variable_count": 2, "header": "image",
      "body": "Olá {{1}}! Use o cupom {{2}} até domingo.",
      "components": [
        { "type": "HEADER", "format": "IMAGE" },
        { "type": "BODY", "text": "Olá {{1}}! Use o cupom {{2}} até domingo." }
      ]
    }
  ]
}`,
      },
      {
        method: 'GET',
        path: '/api/v1/external/channels',
        desc: 'Lista os canais (números) do tenant pra escolher de qual disparar. Envio de template exige channel_type "meta_official" (Cloud API) com status "active".',
        params: [
          {
            name: 'Authorization',
            type: 'string',
            location: 'header',
            required: true,
            desc: 'Bearer SUA_CHAVE.',
          },
        ],
        example: `{
  "channels": [
    { "id": 12, "display_number": "+55 61 9590-0783", "display_name": "Comercial",
      "channel_type": "meta_official", "status": "active" },
    { "id": 13, "display_number": "556132545598", "display_name": "Suporte",
      "channel_type": "qr_code", "status": "active" }
  ]
}`,
      },
    ],
  },
];

interface MessageVar {
  token: string;
  desc: string;
}

/** Variáveis canônicas de mensagem — funcionam em respostas rápidas, chatbot e
 *  campanhas. Espelha a lista do renderer compartilhado (@chatplus/db). */
const MESSAGE_VARS: MessageVar[] = [
  { token: '{{firstname}}', desc: 'Primeiro nome do contato (1ª palavra do nome).' },
  { token: '{{name}}', desc: 'Nome completo do contato. Alias: {{full_name}}.' },
  { token: '{{phone}}', desc: 'Telefone do contato.' },
  { token: '{{email}}', desc: 'E-mail do contato.' },
  { token: '{{state_uf}}', desc: 'UF (estado) do contato.' },
  {
    token: '{{custom.CAMPO}}',
    desc: 'Campo personalizado do contato — troque CAMPO pela chave (ex: {{custom.plano}}).',
  },
  {
    token: '{{atendente}}',
    desc: 'Nome do atendente logado (apenas em respostas rápidas). Aliases: {{agente}}, {{user.name}}.',
  },
];

export function ApiDocsTab() {
  return (
    <section className="flex min-w-0 flex-col gap-4">
      <header>
        <h2
          className="font-bold text-[20px] text-n-900"
          style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
        >
          Documentação
        </h2>
        <p className="text-[13px] text-n-500">
          Endpoints da API externa para integrações (n8n e outros). Autentique com{' '}
          <code className="rounded bg-n-100 px-1 py-0.5 font-mono text-[11px]">
            Authorization: Bearer SUA_CHAVE
          </code>{' '}
          (a chave é criada por um administrador na aba <b>Chave de API</b>). Base:{' '}
          <code className="font-mono text-[11px]">{apiBase()}</code>
        </p>
      </header>

      <div className="flex min-w-0 flex-col gap-3">
        <CollapseCard
          title="Variáveis de mensagem"
          subtitle="Personalização em respostas rápidas, chatbot e campanhas"
        >
          <p className="text-[12px] text-n-600">
            Use estes tokens no texto — são substituídos pelos dados do contato no envio. Token sem
            valor vira vazio.
          </p>
          <div className="min-w-0 overflow-hidden rounded-md border border-n-200">
            <div className="grid grid-cols-[minmax(120px,200px)_1fr] gap-3 border-b border-n-200 bg-n-50 px-3 py-2">
              <span className="text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
                Variável
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
                Descrição
              </span>
            </div>
            {MESSAGE_VARS.map((v) => (
              <div
                key={v.token}
                className="grid grid-cols-[minmax(120px,200px)_1fr] gap-3 border-b border-n-100 px-3 py-2.5 last:border-b-0"
              >
                <code className="break-all font-mono text-[13px] font-semibold text-amber-800">
                  {v.token}
                </code>
                <div className="min-w-0 break-words text-[12px] text-n-600">{v.desc}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-n-500">
            No <b>chatbot</b>, também há <code className="font-mono">{'{{state.X}}'}</code>{' '}
            (variável capturada no fluxo) e <code className="font-mono">{'{{message.body}}'}</code>{' '}
            (última mensagem do contato). Nos <b>eventos de mensagem</b>, use os campos do payload
            recebido (ex: <code className="font-mono">{'{{nome}}'}</code>).
          </p>
        </CollapseCard>

        {ENDPOINT_GROUPS.map((g) => (
          <CollapseCard key={g.title} title={g.title} subtitle={g.subtitle}>
            {g.endpoints.map((ep) => (
              <EndpointRow key={`${ep.method} ${ep.path}`} ep={ep} />
            ))}
          </CollapseCard>
        ))}
      </div>
    </section>
  );
}

function CollapseCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-w-0 overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-n-50"
      >
        <div className="min-w-0">
          <h3 className="font-bold text-[16px] text-n-900">{title}</h3>
          <p className="text-[12px] text-n-500">{subtitle}</p>
        </div>
        <ChevronDown
          size={18}
          className={`flex-shrink-0 text-n-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="flex min-w-0 flex-col gap-2 border-t border-n-100 p-3">{children}</div>
      )}
    </div>
  );
}

const METHOD_STYLE: Record<EndpointDoc['method'], { badge: string; row: string }> = {
  GET: { badge: 'bg-brand-500 text-white', row: 'border-brand-200 bg-brand-50/40' },
  POST: { badge: 'bg-success-500 text-white', row: 'border-success-200 bg-success-50/40' },
};

function CopyBtn({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }}
      aria-label="Copiar"
      title="Copiar"
      className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-n-500 hover:bg-white/70 hover:text-n-800"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

function EndpointRow({ ep }: { ep: EndpointDoc }) {
  const [open, setOpen] = useState(false);
  const style = METHOD_STYLE[ep.method];
  return (
    <div className={`min-w-0 overflow-hidden rounded-md border ${style.row}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full min-w-0 items-center gap-3 overflow-hidden px-3 py-2.5 text-left"
      >
        <span
          className={`w-[52px] flex-shrink-0 rounded px-2 py-0.5 text-center font-mono text-[11px] font-bold ${style.badge}`}
        >
          {ep.method}
        </span>
        <code className="flex-shrink-0 font-mono text-[13px] font-semibold text-n-900">
          {ep.path}
        </code>
        <span className="hidden min-w-0 flex-1 truncate text-[12px] text-n-500 sm:block">
          {ep.desc}
        </span>
        <span className="ml-auto flex flex-shrink-0 items-center gap-1">
          <CopyBtn value={`${apiBase()}${ep.path}`} />
          <ChevronDown
            size={16}
            className={`text-n-400 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      {open && (
        <div className="min-w-0 border-t border-n-200/60 bg-n-0 px-4 py-3">
          <p className="text-[12px] text-n-600">{ep.desc}</p>
          {ep.params && ep.params.length > 0 && (
            <Detail label="Parâmetros">
              <ParamsTable params={ep.params} />
            </Detail>
          )}
          {ep.example && (
            <Detail label="Exemplo de resposta">
              <pre className="min-w-0 overflow-x-auto rounded-md bg-n-900 p-3 text-[11px] leading-relaxed text-n-100">
                <code>{ep.example}</code>
              </pre>
            </Detail>
          )}
        </div>
      )}
    </div>
  );
}

function ParamsTable({ params }: { params: Param[] }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-md border border-n-200">
      <div className="grid grid-cols-[minmax(110px,190px)_1fr] gap-3 border-b border-n-200 bg-n-50 px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-[.05em] text-n-500">Name</span>
        <span className="text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
          Description
        </span>
      </div>
      {params.map((p) => (
        <div
          key={p.name}
          className="grid grid-cols-[minmax(110px,190px)_1fr] gap-3 border-b border-n-100 px-3 py-2.5 last:border-b-0"
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-1.5">
              <span className="break-all font-semibold text-[13px] text-n-900">{p.name}</span>
              {p.required && (
                <span className="text-[10px] font-semibold text-danger-600">* required</span>
              )}
            </div>
            <div className="font-mono text-[11px] text-n-500">{p.type}</div>
            <div className="text-[11px] italic text-n-400">({p.location})</div>
          </div>
          <div className="min-w-0 break-words text-[12px] text-n-600">{p.desc}</div>
        </div>
      ))}
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-3 min-w-0">
      <p className="mb-1 text-[11px] font-bold uppercase tracking-[.05em] text-n-500">{label}</p>
      {children}
    </div>
  );
}
